import React, { useEffect, useState } from 'react';
import { Modal, TextContainer } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import NotificationBanner from '../NotificationBanner/NotificationBanner';

const ackKey = (token) => `systemMessageAcknowledged:${token}`;

const readAck = (token) => {
  try {
    return sessionStorage.getItem(ackKey(token)) === 'true';
  } catch (e) {
    return false;
  }
};

const urlPattern = /(https?:\/\/[^\s]+)/g;

// Renders a line of text with URLs turned into links.
const linkify = (text) => text.split(urlPattern).map((part, index) => (
  part.match(urlPattern)
    // eslint-disable-next-line react/no-array-index-key
    ? <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="underline">{part}</a>
    // eslint-disable-next-line react/no-array-index-key
    : <React.Fragment key={index}>{part}</React.Fragment>
));

const renderLines = (paragraph) => paragraph.split('\n').map((line, index) => (
  // eslint-disable-next-line react/no-array-index-key
  <React.Fragment key={index}>
    {index > 0 && <br />}
    {linkify(line)}
  </React.Fragment>
));

// The message is plain text. Paragraphs are separated by blank lines: the
// first paragraph is the short banner text, the remaining paragraphs are
// the full text shown in the dialog. A single-paragraph message is used
// for both.
const SystemMessage = ({ message, token }) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(() => readAck(token));

  useEffect(() => {
    setAcknowledged(readAck(token));
  }, [token]);

  if (!message) return null;

  const paragraphs = message.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p !== '');
  const bannerParagraph = paragraphs[0];
  const dialogParagraphs = paragraphs.length > 1 ? paragraphs.slice(1) : paragraphs;

  const acknowledge = () => {
    try {
      sessionStorage.setItem(ackKey(token), 'true');
    } catch (e) {
      // sessionStorage unavailable: the dialog simply closes for this render
    }
    setAcknowledged(true);
  };

  return (
    <>
      <div className="m-4">
        <NotificationBanner label={renderLines(bannerParagraph)} color="bg-red-600" />
      </div>
      <Modal
        open={!acknowledged}
        onClose={() => {}}
        title={t('shared.important_notice')}
        primaryAction={{ content: t('shared.i_understand'), onAction: acknowledge }}
      >
        <Modal.Section>
          <TextContainer>
            {/* eslint-disable-next-line react/no-array-index-key */}
            {dialogParagraphs.map((paragraph, index) => <p key={index}>{renderLines(paragraph)}</p>)}
          </TextContainer>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default SystemMessage;
